import React from 'react';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';

import {
  InstantReflex,
  ReflexFeedbackRating,
  ReflexStep,
} from 'shiftNotes/_types';
import * as Styled from 'shiftNotes/styles/NotesStack.style';

import NoteCardReflexStepContent from '../NotesStack/NoteCardReflexStepContent';

import StepRatingControls from './StepRatingControls';
import SubmissionResult from './SubmissionResult';
import SubmitStep from './SubmitStep';
import { StepFeedback } from './types';

interface SectionBodyProps {
  isLoadingVersion: boolean;
  showResult: boolean;
  showThanks: boolean;
  isSubmitStep: boolean;
  isEmptyReflex: boolean;
  isLocked: boolean;
  isViewingOldVersion: boolean;
  overallComment: string;
  onOverallCommentChange: (value: string) => void;
  activeReflex: InstantReflex | undefined;
  activeStep: ReflexStep | null | undefined;
  noteId: string;
  feedback: Record<string, StepFeedback>;
  onRatingChange: (stepId: string, rating: ReflexFeedbackRating) => void;
  onCommentChange: (stepId: string, comment: string) => void;
}

export default function SectionBody({
  isLoadingVersion,
  showResult,
  showThanks,
  isSubmitStep,
  isEmptyReflex,
  isLocked,
  isViewingOldVersion,
  overallComment,
  onOverallCommentChange,
  activeReflex,
  activeStep,
  noteId,
  feedback,
  onRatingChange,
  onCommentChange,
}: SectionBodyProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');

  if (isLoadingVersion) {
    return (
      <Styled.LoadingPill role="status" aria-live="polite">
        <Styled.LoadingPillIcon aria-hidden="true">
          <General.Loading02 />
        </Styled.LoadingPillIcon>
        <Styled.LoadingPillLabel>
          {t('instantReflex.loading')}
        </Styled.LoadingPillLabel>
      </Styled.LoadingPill>
    );
  }

  if (showResult) {
    return <SubmissionResult state={showThanks ? 'thanks' : 'regenerating'} />;
  }

  if (isSubmitStep) {
    return (
      <SubmitStep
        overallComment={overallComment}
        onOverallCommentChange={onOverallCommentChange}
        disabled={isLocked}
        description={
          isEmptyReflex ? t('reflexFeedback.emptyReflexDescription') : undefined
        }
        placeholder={
          isEmptyReflex ? t('reflexFeedback.emptyReflexPlaceholder') : undefined
        }
      />
    );
  }

  if (!activeReflex || !activeStep) return null;

  return (
    <>
      <NoteCardReflexStepContent
        step={activeStep}
        instantReflex={activeReflex}
        noteId={noteId}
      />
      {!isViewingOldVersion && (
        <StepRatingControls
          rating={feedback[activeStep.id]?.rating ?? null}
          comment={feedback[activeStep.id]?.comment ?? ''}
          onRatingChange={(rating) => onRatingChange(activeStep.id, rating)}
          onCommentChange={(comment) => onCommentChange(activeStep.id, comment)}
          disabled={isLocked}
        />
      )}
    </>
  );
}
